import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TableCell from '@mui/material/TableCell';

import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import TextField from '@mui/material/TextField';
import useLanguage from '../../../../components/_hook/useLanguage';
import { IProductSchema } from '../../../../../graphql/type/product';
import CopyIconButton from '../../../../components/_iconButton/CopyIconButton';
import useRoutes from '../../../../components/_hook/useRoutes';
import BaseIconButton from '../../../../components/_iconButton/_module/BaseIconButton';

interface IItem extends IProductSchema {
  key: string;
  variationId?: string;
  stockInStock?: number;
  stockLeadTime?: number;
  variation?: IProductSchema['variations'][0];
}

interface IProps {
  item: IItem;
  setTableRefreshKey?: (tableRefreshKey: number) => void;
  isEditable?: boolean;
  onChange?: (item: IItem) => void;
}

export default function DropshippingProductInventoryManagementTableItem(
  props: IProps
) {
  const { t } = useTranslation();
  const { item, setTableRefreshKey, isEditable, onChange } = props;
  const { path } = useRoutes();
  const { getText } = useLanguage();
  const [stockInStock, setStockInStock] = useState<number>(
    item.stockInStock || 0
  );
  const [stockLeadTime, setStockLeadTime] = useState<number>(
    item.stockLeadTime || 0
  );

  useEffect(() => {
    setStockInStock(item.stockInStock || 0);
    setStockLeadTime(item.stockLeadTime || 0);
  }, [JSON.stringify(item)]);

  return (
    <>
      <TableCell align="left">
        {Boolean(item.variation) && (
          <>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <BaseIconButton size="small">
              <SubdirectoryArrowRightIcon fontSize="small" />
            </BaseIconButton>
          </>
        )}
        <Tooltip title={item._id}>
          <Button
            color="primary"
            component={Link}
            to={path('product.information', {
              companyID: item.company,
              docID: item._id,
            })}
          >
            {item._id.substr(0, 6)}
          </Button>
        </Tooltip>
        <CopyIconButton iconHtmlFontSize="0.8rem" copyText={item._id} />
      </TableCell>
      <TableCell align="left">
        {getText(item.name)}
        {item.variation && item.variation.attributes && (
          <>&nbsp;-&gt;&nbsp;{item.variation.attributes.sku}</>
        )}
      </TableCell>
      <TableCell align="center">
        <TextField
          disabled={!isEditable}
          label={t('in_stock')}
          type="number"
          name="productInventoryInStock"
          value={stockInStock}
          InputLabelProps={{ shrink: true }}
          onChange={(e) => {
            const value = parseInt(e.target.value, 10);
            setStockInStock(value);
            if (onChange) {
              onChange({
                ...item,
                ...{
                  stockInStock: value,
                  stockLeadTime,
                },
              });
            }
          }}
        />
      </TableCell>
      <TableCell align="center">
        <TextField
          label={t('lead_time (day)')}
          disabled={!isEditable}
          type="number"
          name="productInventoryLeadTime"
          value={stockLeadTime}
          InputLabelProps={{ shrink: true }}
          onChange={(e) => {
            const value = parseInt(e.target.value, 10);
            setStockLeadTime(value);
            if (onChange) {
              onChange({
                ...item,
                ...{
                  stockInStock,
                  stockLeadTime: value,
                },
              });
            }
          }}
        />
      </TableCell>
    </>
  );
}
